import { isFlyingTulip } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Flying Tulip — a CEX-style cross-margin account, and the only lender we carry
 * whose **liquidation trigger is not `HF < 1`**.
 *
 * Four things the generic pool builder gets wrong here, every one of which is a
 * statement a borrower would act on:
 *
 *  1. **Liquidation fires at HF < 1.25**, not HF < 1
 *     (`marginHfTargetBps = 12500`) — `liquidateFlash` reverts above it. A
 *     sheet that inherits the pool default understates the danger zone by 25
 *     points of health, on the side that costs the user money.
 *
 *  2. **A new borrow or withdrawal must leave HF ≥ 1.50**
 *     (`marginHfSafeBps = 15000`). So there are TWO thresholds and the gap
 *     between them is not a safety buffer the user chose — it is the
 *     protocol's, and a position opened exactly at the limit is already only
 *     0.25 of health from liquidation.
 *
 *  3. **A minimum account EQUITY in USD** while any debt exists
 *     (`marginMinEquityUSDWad`), the Curvance `MIN_LOAN_SIZE` shape but on
 *     equity rather than debt — and it is **PER CHAIN**: $250 on Ethereum,
 *     $10 on Sonic. Read it off the descriptor; a pinned constant is wrong on
 *     one of the two chains the moment it is written.
 *
 *  4. **Maintenance margin is charged on the DEBT leg as well as the
 *     collateral**, so there is no per-market LTV — the effective figure is
 *     pair-dependent (`CF_i = 1 − hf·mm_i`, `BF_j = 1/(1 + hf·mm_j)`). The
 *     converter already collapses that into per-asset factors; the sheet's job
 *     is to say the number came from a pair, not from this market alone.
 *
 * Deliberately NOT claimed here: the supply rate is the in-kind leg only
 * (`borrowAPR × utilization`). Flying Tulip additionally pays an epoch-indexed
 * FT emission which we do not model, because the protocol's own oracle does
 * not price FT — so `depositRate` is a FLOOR and the sheet must not imply it
 * is the whole return.
 */

function numOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v)
  return undefined
}

/** WAD USD → a plain USD number, for prose and for a UI gate. */
function wadUsd(v: unknown): number | undefined {
  const s =
    typeof v === 'bigint' || typeof v === 'number' ? String(v) : (v as string)
  if (typeof s !== 'string' || !/^\d+$/.test(s)) return undefined
  const n = Number(BigInt(s) / 10n ** 14n) / 1e4
  return Number.isFinite(n) && n > 0 ? n : undefined
}

export const flyingTulipAdapter: TermAdapter = {
  id: 'flying-tulip',
  matches: isFlyingTulip,
  profileId: () => 'flyingTulip.crossMargin@v1',
  build: (input) => {
    const ft = (input.market?.flyingTulip ?? {}) as Record<string, unknown>

    const hfTargetBps = numOrUndef(ft.hfTargetBps)
    const hfSafeBps = numOrUndef(ft.hfSafeBps)
    const minEquityUsd = wadUsd(ft.minEquityUSDWad)

    // Health-factor thresholds as RATIOS (1.25 / 1.50), which is how every
    // consumer compares them against a live HF.
    const liqHf = hfTargetBps != null ? hfTargetBps / 1e4 : undefined

    return {
      constraints: {
        // One account per address per chain, spanning every asset — there is
        // no per-market position and no id.
        positionModel: 'account',
        positionIdMeaning:
          'the borrower address itself — one cross-margin account per chain ' +
          'covering every asset, with a single global health check',
        crossMargin: true,
        // NOTE the USD equity floor ($250 Ethereum / $10 Sonic) has no
        // structured slot — `availability.minSize` is a DEBT floor in asset
        // units and this is an EQUITY floor in USD, so putting it there would
        // be read as the wrong gate on the wrong quantity. It is stated in the
        // profile prose instead, and the raw value stays on
        // `params.market.flyingTulip.minEquityUSDWad` for a caller that needs
        // to gate on it.
      },
      borrow: {
        debtShape: 'accruing',
        // The HF >= 1.50 open gate likewise has no structured slot; it is in
        // the profile prose. `availability` here carries no minimum size —
        // Flying Tulip has none on the debt leg.
        exit: {
          // Repay is always open — `repayFor` is permissionless, so a third
          // party can rescue the account, and nothing gates or delays it.
          partialAllowed: true,
          cooldownSecs: undefined,
        },
        ...(liqHf != null
          ? {
              liquidation: {
                model: 'partial',
                trigger: 'price',
                // THE correction. Everything else in this repo may assume
                // `HF < 1 ⇒ liquidatable`; here the protocol reverts above
                // 1.25, so the position is liquidatable a quarter of a point
                // of health EARLIER than the default reading.
                liquidationHealthFactor: liqHf,
              },
            }
          : {}),
      },
      supply: {
        exit: {
          // A withdrawal is checked against the SAME open gate as a borrow
          // (HF >= 1.50) and against the equity floor.
        },
      },
    }
  },
}
